/**
 * Dentist absent date: appointments booked in the absent time must be
 * rescheduled or cancelled before the absence saves. Backend rules:
 * multidental-backend docs/dentist-absent-clear-appointments.md
 */

/** Save refused because patients are booked in the time. */
export const ABSENT_HAS_APPOINTMENTS = "ABSENT_HAS_APPOINTMENTS";

export type ClashKind = "movable" | "in_clinic";

export type AbsentClash = {
  _id: string;
  kind: ClashKind;
  date: string;
  startTime: string;
  endTime: string;
  status: string;
  bookingType: string;
  sessionName: string;
  queue: number;
  patientName: string;
  regNo: string;
  mobileNo: string;
  purposes: string[];
};

/** SMS outcome as the backend returns it (services/sms/sms.js). */
export type SmsOutcome = {
  attempted?: boolean;
  sent?: boolean;
  message?: string | null;
};

export type RescheduleSlot = {
  startTime: string;
  endTime: string;
  sessionName: string;
};

/** A patient the clinic must phone because the SMS did not go out. */
export type CallNote = {
  appointmentId: string;
  patientName: string;
  mobileNo: string;
  action: "rescheduled" | "cancelled";
  detail: string;
  reason: string;
};

function str(v: unknown): string {
  return v == null ? "" : String(v).trim();
}

export function parseClashes(raw: unknown): AbsentClash[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((x) => {
      const o = (x ?? {}) as Record<string, unknown>;
      const p = (o.patient ?? {}) as Record<string, unknown>;
      const title = str(p.title).replace(/\.$/, "");
      const name = str(p.fullName);
      return {
        _id: str(o._id),
        kind: (o.kind === "in_clinic" ? "in_clinic" : "movable") as ClashKind,
        date: str(o.date),
        startTime: str(o.startTime).slice(0, 5),
        endTime: str(o.endTime).slice(0, 5),
        status: str(o.status),
        bookingType: str(o.bookingType),
        sessionName: str(o.sessionName),
        queue: Number(o.queue) || 0,
        patientName: title && name ? `${title}. ${name}` : name || "Unknown patient",
        regNo: str(p.regNo),
        mobileNo: str(p.mobileNo),
        purposes: Array.isArray(o.purposes) ? o.purposes.map(str).filter(Boolean) : [],
      };
    })
    .filter((c) => c._id);
}

const BOOKING_LABELS: Record<string, string> = {
  walk_in: "Walk-in",
  other: "Other",
  online: "Online",
};

/** Chip text for non-standard bookings; "" for a normal booked appointment. */
export function bookingTypeLabel(bookingType: string): string {
  return BOOKING_LABELS[str(bookingType).toLowerCase()] ?? "";
}

export function movableClashes(clashes: AbsentClash[]): AbsentClash[] {
  return clashes.filter((c) => c.kind === "movable");
}

/** Why no SMS went out, or "" when it was sent. */
export function smsProblem(sms: SmsOutcome | null | undefined): string {
  if (sms?.sent) return "";
  return str(sms?.message) || "SMS was not sent";
}

function minutesOf(t: string): number {
  const m = /^(\d{1,2}):(\d{2})/.exec(str(t));
  return m ? Number(m[1]) * 60 + Number(m[2]) : NaN;
}

function ymdOf(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;
}

/** Drop start times already past when the chosen day is today; earlier days offer nothing. */
export function futureSlots(slots: RescheduleSlot[], ymd: string, now: Date): RescheduleSlot[] {
  const today = ymdOf(now);
  if (ymd < today) return [];
  if (ymd > today) return slots;
  const nowM = now.getHours() * 60 + now.getMinutes();
  return slots.filter((s) => minutesOf(s.startTime) > nowM);
}

/** "HH:mm" → "HH:mm:00", the format the appointment API stores. */
export function toBackendTime(hm: string): string {
  const s = str(hm);
  if (/^\d{2}:\d{2}:\d{2}$/.test(s)) return s;
  return /^\d{1,2}:\d{2}$/.test(s) ? `${s.padStart(5, "0")}:00` : s;
}

export function minutesBetween(start: string, end: string): number {
  const d = minutesOf(end) - minutesOf(start);
  return Number.isFinite(d) && d > 0 ? d : 0;
}

/** 12-hour label for a "HH:mm" time, e.g. "2:30 PM". */
export function formatTime12(hm: string): string {
  const total = minutesOf(hm);
  if (!Number.isFinite(total)) return str(hm);
  const h = Math.floor(total / 60);
  const m = total % 60;
  const suffix = h >= 12 ? "PM" : "AM";
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${suffix}`;
}

export function formatIsoDate(ymd: string): string {
  const [y, mo, d] = str(ymd).split("-").map(Number);
  if (!y || !mo || !d) return str(ymd);
  return new Date(y, mo - 1, d).toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
}

/** Text for the toast after a bulk cancel. */
export function cancelSummary(
  results: Array<{ cancelled?: boolean; error?: string; sms?: SmsOutcome; alreadyCancelled?: boolean }>
): { cancelled: number; failed: number; notTexted: number } {
  let cancelled = 0;
  let failed = 0;
  let notTexted = 0;
  for (const r of results ?? []) {
    if (!r?.cancelled) {
      failed += 1;
      continue;
    }
    cancelled += 1;
    if (!r.alreadyCancelled && smsProblem(r.sms)) notTexted += 1;
  }
  return { cancelled, failed, notTexted };
}
